import { useCallback, useEffect, useState } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import UserImg from "@styles/all-images/icons/bg-user.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import { useNavigate, useParams } from "react-router-dom";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import { toast } from "react-toastify";
import DismissIcon from "@styles/all-images/icons/dismiss.svg";
import { Skeleton } from "@mui/material";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import { usePermissionState } from "@/providers/PermissionProvider";
import { LiveChatPermissions } from "@/constants/permission-modules/base-permissions";

interface GroupMember {
  id: number;
  name: string;
  chat_type: number;
  members: string;
  created_by: string;
  created_at: string;
  is_terminated: boolean;
  profiles: Profile[];
  user_type: {
    id: number;
    name: string;
  };
  is_admin: boolean;
  profile_picture: string;
  is_online: boolean;
}
interface Profile {
  id: number;
  name: string;
}
interface GroupInfo {
  room_id: number;
  name: string;
  users: string;
}

const ManagementTeamsGroupMembers = () => {
  const { formatDateWithTime } = dateHandler();
  const dispatch = useDispatch<AppThunkDispatch>();
  const navigate = useNavigate();
  const { id } = useParams(); // Get group ID from URL

  const [forceRefresh, setForceRefresh] = useState(false);
  const [forceReset, setForceReset] = useState(false);
  const [columns, setColumns] = useState<any[]>([]);
  const [groupNameLoading, setGroupNameLoading] = useState(true);
  const [groupName, setGroupName] = useState("");
  const { isPermitted } = usePermissionState();

  // Fetch group details for the header
  const fetchGroupDetails = useCallback(async () => {
    if (!id) return;
    try {
      setGroupNameLoading(true);
      const { authenticatedCall } = actionsHandler("createGroupChatById");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [`${id}/`],
        }),
      );

      if (response.ok) {
        setGroupName(response.data.name);
      }
    } catch (error) {
      console.error("Error fetching group details:", error);
    } finally {
      setGroupNameLoading(false);
    }
  }, [dispatch, id]);

  const handleSettingColumns = useCallback(() => {
    setColumns([
      {
        header: "Name",
        accessor: (row: GroupMember) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-w-[35px] tw-h-[35px] tw-bg-[#ECF1F8] tw-rounded-full tw-overflow-hidden">
              <img
                src={row.profile_picture || UserImg}
                alt="Profile"
                className="tw-w-10 tw-h-10 tw-rounded-full"
              />
            </div>
            <span
              className={`tw-rounded-full tw-w-2 tw-h-2 ${row.is_online ? "tw-bg-[#12B76A]" : "tw-bg-[#717680]"}`}
            ></span>
            <span>{row.name}</span>
          </div>
        ),
        sortable: true,
        sortKey: "name",
      },
      {
        header: "Company Name",
        accessor: "company_name",
        sortable: true,
        sortKey: "company_name",
      },
      {
        header: "Type",
        accessor: (row: GroupMember) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span>{`T${row.user_type.id}`}</span>
            {row.is_admin && (
              <span className="tw-text-[#12B76A]">Group Admin</span>
            )}
          </div>
        ),
        sortable: true,
        sortKey: "user_type",
      },
      {
        header: "Profile/Role",
        accessor: (row: GroupMember) =>
          row.profiles.map((profile: any) => profile.name).join(", "),
        sortable: true,
        sortKey: "profile_role",
      },
      {
        header: "Action",
        accessor: (row: GroupMember) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            {isPermitted(LiveChatPermissions.DELETE_LIVE_CHAT) &&
              !row.is_admin && (
                <button
                  onClick={() => handleTerminate(row.id)}
                  title="Terminate"
                  className="tw-p-2 hover:tw-bg-gray-100 tw-rounded-full"
                >
                  <img
                    src={DismissIcon}
                    alt="Terminate"
                    className="tw-w-5 tw-h-5"
                  />
                </button>
              )}
            {row.is_admin && (
              <span className="tw-px-4 tw-py-2">{NO_DATA_AVAILABLE}</span>
            )}
          </div>
        ),
      },
    ]);
  }, []);

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { getAllData } = actionsHandler("getGroupChatMembersById");
        let ordering = "";
        if (params.sortColumn) {
          ordering =
            params.sortDirection === "desc"
              ? `-${params.sortColumn}`
              : params.sortColumn;
        }
        const response = await dispatch(
          getAllData({
            method: "get",
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
            customParams: [`${id}/`],
            customQuery: ordering ? `ordering=${ordering}` : "",
          }),
        );

        // Transform the response to match the table's expected format
        if (response.ok) {
          return response;
        }
      } catch (error) {
        console.error("Error fetching group members:", error);
        return {};
      }
    },
    [dispatch],
  );

  const handleTerminate = async (memberId: number) => {
    try {
      const { authenticatedCall } = actionsHandler("removeGroupMember");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          formattingParams: {
            id: id?.toString() || "",
          },
          payload: {
            member_ids: [memberId.toString()],
          },
        }),
      );

      if (response.ok) {
        toast.success("Member terminated successfully");
        setForceRefresh(true); // Refresh the table
      } else {
        toast.error(response.data?.message || "Failed to terminate member");
      }
    } catch (error) {
      console.error("Error terminating member:", error);
      toast.error("Failed to terminate member");
    }
  };

  useEffect(() => {
    if (id) {
      // Only run if id exists
      handleSettingColumns();
      fetchGroupDetails();
      // setForceReset(true);
    }
  }, []);

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                onClick={() => navigate(`/live-chat?screen=group&id=${id}`)}
                className="tw-cursor-pointer"
              />
              {groupNameLoading ? (
                <Skeleton width={500} height={32} className="tw-mx-2" />
              ) : (
                <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323] tw-flex tw-items-center">
                  Members of <span className="tw-mx-2">'{groupName}'</span>{" "}
                  group
                </h1>
              )}
            </div>
            <div>
              <button
                onClick={() =>
                  navigate(`/add-people-group-live-chat/${id}?list=true`)
                }
                className="tw-bg-[#146C94] tw-py-3 tw-px-4 tw-text-white tw-text-sm tw-font-semibold tw-rounded-lg tw-flex tw-items-center tw-gap-2"
              >
                <img
                  src={PlusIcon}
                  alt="Add Icon"
                  className="tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6 tw-max-w-6 tw-max-h-6"
                />
                Add Member
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-mt-[147px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
          forceReset={forceReset}
          setForceReset={setForceReset}
          noDataMessage="No members found in this group"
          noSearchDataMessage="No matching members found"
        />
      </div>
    </div>
  );
};

export default ManagementTeamsGroupMembers;
